import apiClient from './api.service';
import type { RejectionPayload } from './rejection.service';

/** Same as calibration.service: ResponseUtil.success { success, data, message } */
function unwrapBody<T>(res: unknown): T {
  if (res == null || typeof res !== 'object') return res as T;
  const r = res as Record<string, unknown>;
  const inner = r.data;
  if (inner != null && typeof inner === 'object' && 'data' in (inner as object)) {
    return (inner as { data: T }).data;
  }
  if ('data' in r && (r.success === true || r.status === 'success')) {
    return r.data as T;
  }
  return res as T;
}

function unwrapList<T>(res: unknown): T[] {
  const list = unwrapBody<T[] | undefined>(res);
  return Array.isArray(list) ? list : [];
}

// =====================================================
// QUALITY PLAN TYPES
// =====================================================
export interface QualityPlan {
  id: string;
  company_id: string;
  project_id?: string;
  work_order_id?: string;
  template_id?: string;
  plan_number: string;
  version: string;
  revision_number: number;
  status: 'Draft' | 'Under Review' | 'Approved' | 'Active' | 'Superseded' | 'Cancelled';
  approval_date?: string;
  approved_by?: string;
  approval_notes?: string;
  effective_date?: string;
  expiry_date?: string;
  review_date?: string;
  department_id?: string | null;
  created_by?: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
  project?: any;
  work_order?: { id: string; work_order_number?: string; work_order_status?: string };
  template?: any;
  items?: QualityPlanItem[];
}

export interface QualityPlanItem {
  id: string;
  company_id: string;
  quality_plan_id: string;
  inspection_point_id?: string;
  sequence_number: number;
  parent_item_id?: string;
  level: number;
  inspection_type: 'Hold Point' | 'Witness Point' | 'Review Point' | 'Standard';
  description: string;
  acceptance_criteria?: string;
  required_personnel_qualification?: string;
  assigned_inspector_id?: string;
  witness_required: boolean;
  witness_party?: string;
  mandatory: boolean;
  can_skip: boolean;
  status: 'Pending' | 'Scheduled' | 'In Progress' | 'Completed' | 'Skipped' | 'On Hold';
  scheduled_date?: string;
  completed_date?: string;
  created_at: string;
  updated_at: string;
  quality_plan?: { id: string; plan_number?: string };
}

// =====================================================
// INSPECTION TYPES
// =====================================================
export interface InspectionRecord {
  id: string;
  company_id: string;
  project_id: string;
  quality_plan_id?: string;
  quality_plan_item_id?: string;
  inspection_point_id?: string;
  inspection_number: string;
  inspection_type: 'Hold Point' | 'Witness Point' | 'Review Point' | 'Standard';
  scheduled_date?: string;
  scheduled_time?: string;
  actual_date?: string;
  actual_time?: string;
  inspector_id: string;
  inspector_name?: string;
  inspector_qualification?: string;
  status:
    | 'Scheduled'
    | 'In Progress'
    | 'Completed'
    | 'On Hold'
    | 'Cancelled'
    | 'Rescheduled'
    | 'Pending Approval';
  result: 'Pass' | 'Fail' | 'Conditional' | 'Pending' | 'N/A';
  inspection_category?: string;
  inspection_subcategory?: string;
  source_document_type?: string | null;
  source_document_id?: string | null;
  dimensional_inspection_id?: string | null;
  dimensional_bypass_at?: string | null;
  dimensional_bypass_by?: string | null;
  dimensional_bypass_reason?: string | null;
  lot_quantity?: number | null;
  sample_size?: number | null;
  sampling_plan?: string | null;
  approval_instance_id?: string | null;
  status_before_approval?: string | null;
  pending_result?: string | null;
  department_id?: string | null;
  findings?: string;
  observations?: string;
  supporting_documents?: string[];
  photos?: string[];
  witness_required: boolean;
  witness_party?: string;
  witness_name?: string;
  witness_signature?: any;
  witness_date?: string;
  is_hold_point: boolean;
  hold_point_released: boolean;
  released_by?: string;
  released_at?: string;
  ncr_generated: boolean;
  ncr_id?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

// =====================================================
// NCR TYPES
// =====================================================
export interface NCRRecord {
  id: string;
  company_id: string;
  project_id?: string;
  project?: { id: string; project_number?: string; project_name?: string };
  work_order_id?: string | null;
  work_order?: { id: string; work_order_number?: string } | null;
  department_id?: string | null;
  /** Resolved label from Finance departments master (code - name). */
  department_name?: string | null;
  inspection_record_id?: string;
  ncr_number: string;
  source:
    | 'Inspection'
    | 'Internal Audit'
    | 'Customer Complaint'
    | 'Supplier Issue'
    | 'Self-Identified'
    | 'Other'
    | 'Measuring Equipment';
  source_reference?: string;
  asset_id?: string | null;
  supplier_name?: string | null;
  supplier_contact_person?: string | null;
  package_no?: string | null;
  po_number?: string | null;
  po_line?: string | null;
  part_item_no?: string | null;
  part_description?: string | null;
  equipment_description?: string | null;
  tag_number?: string | null;
  drawing_no?: string | null;
  drawing_revision?: string | null;
  specification_number?: string | null;
  reference_documents?: string | null;
  international_standard?: string | null;
  actual_requirements?: string | null;
  additional_information?: string | null;
  proposed_corrective_action?: string | null;
  capa_target_date?: string | null;
  capa_delay_justification?: string | null;
  opportunity_for_improvement?: boolean;
  customer_comments?: string | null;
  customer_decision?: string | null;
  customer_decision_date?: string | null;
  supplier_signer_name?: string | null;
  supplier_signer_title?: string | null;
  customer_signer_name?: string | null;
  customer_signer_title?: string | null;
  severity: 'Critical' | 'Major' | 'Minor';
  category?: string;
  description: string;
  location?: string;
  affected_items?: string;
  quantity_affected?: number;
  quantity_uom?: string | null;
  identified_by: string;
  identifiedBy?: { id: string; first_name?: string; last_name?: string; email?: string };
  next_action_user_id?: string | null;
  correction_owner_user_id?: string | null;
  capa_owner_user_id?: string | null;
  moc_required?: boolean;
  moc_id?: string | null;
  moc_ref_no?: string | null;
  risk_assessment_id?: string | null;
  risk_reference?: string | null;
  is_potential_nc?: boolean;
  containment_action?: string | null;
  identified_date: string;
  identified_time?: string;
  status: 'Open' | 'Under Investigation' | 'Root Cause Analysis' | 'CAPA Assigned' | 'Corrective Action' | 'Pending Approval' | 'Verification' | 'Closed' | 'Rejected';
  approval_instance_id?: string | null;
  status_before_approval?: string | null;
  disposition:
    | 'Use As Is'
    | 'Rework'
    | 'Repair'
    | 'Reject'
    | 'Scrap'
    | 'Pending'
    | 'Regrade'
    | 'Release Under Concession';
  disposition_approved_by?: string;
  disposition_date?: string;
  disposition_notes?: string;
  disposition_done_by?: string | null;
  correction_target_date?: string | null;
  correction_action?: string | null;
  correction_completed_by?: string | null;
  correction_completed_date?: string | null;
  correction_delay_justification?: string | null;
  concession_rationale?: string | null;
  concession_party_notified?: boolean | null;
  concession_evidence?: string | null;
  concession_approved_by_name?: string | null;
  concession_approved_at?: string | null;
  root_cause_analysis?: string;
  root_cause_method?: string;
  root_cause_identified_by?: string;
  root_cause_date?: string;
  root_cause_verified: boolean;
  capa_required: boolean;
  capa_id?: string;
  capa?: CAPARecord;
  root_causes?: NCRRootCauseAnalysis[];
  effectiveness_verified: boolean;
  effectiveness_verified_by?: string;
  effectiveness_verified_date?: string;
  effectiveness_notes?: string;
  verification_method?: string | null;
  verified_by_designation?: string | null;
  verification_evidence_notes?: string | null;
  closed_by?: string;
  closed_date?: string;
  closure_notes?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface NCRRootCauseAnalysis {
  id: string;
  company_id: string;
  ncr_id: string;
  sequence: number;
  root_cause_method?: string | null;
  root_cause_analysis: string;
  root_cause_identified_by?: string | null;
  root_cause_date?: string | null;
  root_cause_verified: boolean;
  verified_by?: string | null;
  verified_at?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

// =====================================================
// CAPA TYPES
// =====================================================
export interface CAPARecord {
  id: string;
  company_id: string;
  ncr_id?: string;
  project_id?: string;
  department_id?: string | null;
  /** Resolved label from Finance departments master (code - name). */
  department_name?: string | null;
  capa_number: string;
  source: 'NCR' | 'Internal Audit' | 'Management Review' | 'Preventive Action' | 'Customer Feedback' | 'Other';
  source_reference?: string;
  type: 'Corrective Action' | 'Preventive Action' | 'Both';
  description: string;
  problem_statement?: string;
  action_plan: string;
  action_steps?: any[];
  responsible_person_id: string;
  responsible_person_name?: string;
  supporting_personnel?: string[];
  target_completion_date: string;
  actual_completion_date?: string;
  extended_date?: string;
  extension_reason?: string;
  status: 'Assigned' | 'In Progress' | 'Completed' | 'Pending Approval' | 'Effectiveness Verification' | 'Closed' | 'Cancelled';
  progress_percentage: number;
  progress_notes?: string;
  effectiveness_criteria?: string;
  effectiveness_verified: boolean;
  effectiveness_verified_by?: string;
  effectiveness_verified_date?: string;
  effectiveness_notes?: string;
  effectiveness_rating?: 'Effective' | 'Partially Effective' | 'Not Effective';
  closed_by?: string;
  closed_date?: string;
  closure_notes?: string;
  approval_instance_id?: string | null;
  status_before_approval?: string | null;
  created_by?: string;
  created_at: string;
  updated_at: string;
  ncr?: {
    id?: string;
    ncr_number?: string;
    description?: string;
    disposition?: string;
  };
  project?: any;
}

// =====================================================
// AUDIT SCHEDULE TYPES
// =====================================================
export interface AuditScheduleRecord {
  id: string;
  company_id: string;
  schedule_number: string;
  audit_title: string;
  audit_type: string;
  scheduled_start_date: string;
  scheduled_end_date: string;
  status: string;
  lead_auditor_id: string;
  created_at?: string;
  updated_at?: string;
}

// =====================================================
// QUALITY SERVICE
// =====================================================
class QualityService {
  // Quality Plans
  async getQualityPlans(filters?: { project_id?: string; status?: string }): Promise<QualityPlan[]> {
    try {
      console.log('🔍 Fetching quality plans with filters:', filters);
      const response = await apiClient.get('/quality/quality-plans', { params: filters });
      console.log('📦 Full API Response:', JSON.stringify(response, null, 2));
      const plans = unwrapList<QualityPlan>(response);
      console.log('✅ Returning plans:', plans.length);
      return plans;
    } catch (error: any) {
      console.error('❌ Error fetching quality plans:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      throw error;
    }
  }

  async getQualityPlanById(id: string): Promise<QualityPlan> {
    const response = await apiClient.get(`/quality/quality-plans/${id}`);
    return unwrapBody<QualityPlan>(response);
  }

  async createQualityPlan(data: Partial<QualityPlan>): Promise<QualityPlan> {
    const response = await apiClient.post('/quality/quality-plans', data);
    return unwrapBody<QualityPlan>(response);
  }

  async updateQualityPlan(id: string, data: Partial<QualityPlan>): Promise<QualityPlan> {
    const response = await apiClient.put(`/quality/quality-plans/${id}`, data);
    return unwrapBody<QualityPlan>(response);
  }

  async approveQualityPlan(id: string, notes?: string): Promise<QualityPlan> {
    const response = await apiClient.post(`/quality/quality-plans/${id}/approve`, { notes });
    return unwrapBody<QualityPlan>(response);
  }

  async submitForApproval(id: string): Promise<QualityPlan> {
    const response = await apiClient.post(`/quality/quality-plans/${id}/submit`);
    return unwrapBody<QualityPlan>(response);
  }

  async rejectQualityPlan(id: string, payload: RejectionPayload): Promise<QualityPlan> {
    const response = await apiClient.post(`/quality/quality-plans/${id}/reject`, payload);
    return unwrapBody<QualityPlan>(response);
  }

  async createQualityPlanFromWorkOrder(workOrderId: string, data?: Partial<QualityPlan>): Promise<QualityPlan> {
    const response = await apiClient.post(`/quality/quality-plans/from-work-order/${workOrderId}`, data);
    return unwrapBody<QualityPlan>(response);
  }

  async getQualityPlanByWorkOrder(workOrderId: string): Promise<QualityPlan | null> {
    try {
      const response = await apiClient.get(`/quality/quality-plans/work-order/${workOrderId}`);
      return unwrapBody<QualityPlan | null>(response) ?? null;
    } catch (error: any) {
      if (error.response?.status === 404 || error.response?.data?.data === null) {
        return null;
      }
      throw error;
    }
  }

        async deleteQualityPlan(id: string): Promise<void> {
          await apiClient.delete(`/quality/quality-plans/${id}`);
        }

        // Quality Plan Items
        async getQualityPlanItems(planId: string): Promise<QualityPlanItem[]> {
          const response = await apiClient.get(`/quality/quality-plans/${planId}/items`);
          return unwrapList<QualityPlanItem>(response);
        }

        async getQualityPlanItemById(planId: string, itemId: string): Promise<QualityPlanItem> {
          const response = await apiClient.get(`/quality/quality-plans/${planId}/items/${itemId}`);
          return unwrapBody<QualityPlanItem>(response);
        }

        async createQualityPlanItem(planId: string, data: Partial<QualityPlanItem>): Promise<QualityPlanItem> {
          const response = await apiClient.post(`/quality/quality-plans/${planId}/items`, data);
          return unwrapBody<QualityPlanItem>(response);
        }

        async updateQualityPlanItem(planId: string, itemId: string, data: Partial<QualityPlanItem>): Promise<QualityPlanItem> {
          const response = await apiClient.put(`/quality/quality-plans/${planId}/items/${itemId}`, data);
          return unwrapBody<QualityPlanItem>(response);
        }

        async deleteQualityPlanItem(planId: string, itemId: string): Promise<void> {
          await apiClient.delete(`/quality/quality-plans/${planId}/items/${itemId}`);
        }

        async reorderQualityPlanItems(planId: string, itemIds: string[]): Promise<QualityPlanItem[]> {
          const response = await apiClient.post(`/quality/quality-plans/${planId}/items/reorder`, { itemIds });
          return unwrapList<QualityPlanItem>(response);
        }

        /** Pending inspection items from all quality plans (for Inspections menu) */
        async getPendingQualityPlanItems(): Promise<QualityPlanItem[]> {
          const response = await apiClient.get('/quality/quality-plans/items/pending');
          return unwrapList<QualityPlanItem>(response);
        }

  // Inspections
  async getInspections(filters?: {
    project_id?: string;
    status?: string;
    result?: string;
    inspection_category?: string;
    source_document_type?: string;
  }): Promise<InspectionRecord[]> {
    const response = await apiClient.get('/quality/inspections', { params: filters });
    return unwrapList<InspectionRecord>(response);
  }

  async getInspectionById(id: string): Promise<InspectionRecord> {
    const response = await apiClient.get(`/quality/inspections/${id}`);
    return unwrapBody<InspectionRecord>(response);
  }

  async createInspection(data: Partial<InspectionRecord>): Promise<InspectionRecord> {
    const response = await apiClient.post('/quality/inspections', data);
    return unwrapBody<InspectionRecord>(response);
  }

  async updateInspection(id: string, data: Partial<InspectionRecord>): Promise<InspectionRecord> {
    const response = await apiClient.put(`/quality/inspections/${id}`, data);
    return unwrapBody<InspectionRecord>(response);
  }

  async completeInspection(
    id: string,
    result: string,
    findings?: string,
    opts?: { dimensional_bypass_reason?: string }
  ): Promise<InspectionRecord> {
    const response = await apiClient.post(`/quality/inspections/${id}/complete`, {
      result,
      findings,
      dimensional_bypass_reason: opts?.dimensional_bypass_reason,
    });
    return unwrapBody<InspectionRecord>(response);
  }

  async initiateReceivingDimensionalInspection(receivingInspectionId: string): Promise<InspectionRecord> {
    const response = await apiClient.post(
      `/quality/inspections/${receivingInspectionId}/initiate-receiving-dimensional`
    );
    return unwrapBody<InspectionRecord>(response);
  }

  async replaceInspectionSampling(
    id: string,
    payload: {
      lot_quantity?: number | null;
      sample_size?: number | null;
      sampling_plan?: string | null;
      samples?: Array<{
        id?: string;
        receiving_inspection_detail_id?: string | null;
        sample_number?: number;
        sample_qty?: number;
        status?: string;
        notes?: string | null;
      }>;
    }
  ): Promise<InspectionRecord> {
    const response = await apiClient.put(`/quality/inspections/${id}/sampling`, payload);
    return unwrapBody<InspectionRecord>(response);
  }

  async submitInspectionForApproval(
    id: string,
    payload: {
      result: string;
      findings?: string;
      dimensional_bypass_reason?: string;
    }
  ): Promise<InspectionRecord> {
    const response = await apiClient.post(`/quality/inspections/${id}/submit-for-approval`, payload);
    return unwrapBody<InspectionRecord>(response);
  }

  async deleteInspection(id: string): Promise<void> {
    await apiClient.delete(`/quality/inspections/${id}`);
  }

  // NCRs
  async getNCRs(filters?: { project_id?: string; status?: string; severity?: string }): Promise<NCRRecord[]> {
    const response = await apiClient.get('/quality/ncrs', { params: filters });
    return unwrapList<NCRRecord>(response);
  }

  async getNCRById(id: string): Promise<NCRRecord> {
    const response = await apiClient.get(`/quality/ncrs/${id}`);
    return unwrapBody<NCRRecord>(response);
  }

  async createNCR(data: Partial<NCRRecord>): Promise<NCRRecord> {
    const response = await apiClient.post('/quality/ncrs', data);
    return unwrapBody<NCRRecord>(response);
  }

  async updateNCR(id: string, data: Partial<NCRRecord>): Promise<NCRRecord> {
    const response = await apiClient.put(`/quality/ncrs/${id}`, data);
    return unwrapBody<NCRRecord>(response);
  }

  async closeNCR(id: string, notes?: string): Promise<NCRRecord> {
    const response = await apiClient.post(`/quality/ncrs/${id}/close`, { notes });
    return unwrapBody<NCRRecord>(response);
  }

  async deleteNCR(id: string): Promise<void> {
    await apiClient.delete(`/quality/ncrs/${id}`);
  }

  async updateNCRStatus(id: string, status: string): Promise<NCRRecord> {
    const response = await apiClient.post(`/quality/ncrs/${id}/update-status`, { status });
    return unwrapBody<NCRRecord>(response);
  }

  async submitNCRForApproval(id: string): Promise<NCRRecord> {
    const response = await apiClient.post(`/quality/ncrs/${id}/submit-for-approval`);
    return unwrapBody<NCRRecord>(response);
  }

  async addRootCauseAnalysis(id: string, data: {
    root_cause_analysis: string;
    root_cause_method?: string;
    root_cause_verified?: boolean;
  }): Promise<NCRRecord> {
    const response = await apiClient.post(`/quality/ncrs/${id}/root-cause`, data);
    return unwrapBody<NCRRecord>(response);
  }

  async listRootCauses(ncrId: string): Promise<NCRRootCauseAnalysis[]> {
    const response = await apiClient.get(`/quality/ncrs/${ncrId}/root-causes`);
    return unwrapList<NCRRootCauseAnalysis>(response);
  }

  async addRootCause(
    ncrId: string,
    data: {
      root_cause_analysis: string;
      root_cause_method?: string;
      root_cause_date?: string;
      root_cause_verified?: boolean;
      root_cause_identified_by?: string;
    }
  ): Promise<NCRRootCauseAnalysis> {
    const response = await apiClient.post(`/quality/ncrs/${ncrId}/root-causes`, data);
    return unwrapBody<NCRRootCauseAnalysis>(response);
  }

  async updateRootCause(
    ncrId: string,
    rcaId: string,
    data: Partial<{
      root_cause_analysis: string;
      root_cause_method: string;
      root_cause_date: string;
      root_cause_verified: boolean;
      reopen: boolean;
    }>
  ): Promise<NCRRootCauseAnalysis> {
    const response = await apiClient.put(`/quality/ncrs/${ncrId}/root-causes/${rcaId}`, data);
    return unwrapBody<NCRRootCauseAnalysis>(response);
  }

  async deleteRootCause(ncrId: string, rcaId: string): Promise<void> {
    await apiClient.delete(`/quality/ncrs/${ncrId}/root-causes/${rcaId}`);
  }

  async listNcrCapas(ncrId: string): Promise<CAPARecord[]> {
    const response = await apiClient.get(`/quality/ncrs/${ncrId}/capas`);
    return unwrapList<CAPARecord>(response);
  }

  async setDisposition(id: string, data: {
    disposition: string;
    disposition_notes?: string;
    disposition_done_by?: string | null;
    correction_target_date?: string | null;
    correction_action?: string | null;
    correction_completed_by?: string | null;
    correction_completed_date?: string | null;
    correction_delay_justification?: string | null;
    concession_rationale?: string | null;
    concession_party_notified?: boolean | null;
    concession_evidence?: string | null;
    concession_approved_by_name?: string | null;
    concession_approved_at?: string | null;
  }): Promise<NCRRecord> {
    const response = await apiClient.post(`/quality/ncrs/${id}/disposition`, data);
    return unwrapBody<NCRRecord>(response);
  }

  async requireCAPA(id: string, capaRequired: boolean): Promise<NCRRecord> {
    const response = await apiClient.post(`/quality/ncrs/${id}/require-capa`, { capa_required: capaRequired });
    return unwrapBody<NCRRecord>(response);
  }

  async linkCAPA(id: string, capaId: string): Promise<NCRRecord> {
    const response = await apiClient.post(`/quality/ncrs/${id}/link-capa`, { capa_id: capaId });
    return unwrapBody<NCRRecord>(response);
  }

  async verifyEffectiveness(id: string, data: {
    effectiveness_verified: boolean;
    effectiveness_notes?: string;
    verification_method?: string | null;
    verified_by_designation?: string | null;
    verification_evidence_notes?: string | null;
  }): Promise<NCRRecord> {
    const response = await apiClient.post(`/quality/ncrs/${id}/verify-effectiveness`, data);
    return unwrapBody<NCRRecord>(response);
  }

  // CAPAs
  async getCAPAs(filters?: {
    project_id?: string;
    status?: string;
    type?: string;
    ncr_id?: string;
  }): Promise<CAPARecord[]> {
    const response = await apiClient.get('/quality/capas', { params: filters });
    return unwrapList<CAPARecord>(response);
  }

  async getCAPAById(id: string): Promise<CAPARecord> {
    const response = await apiClient.get(`/quality/capas/${id}`);
    return unwrapBody<CAPARecord>(response);
  }

  async createCAPA(data: Partial<CAPARecord>): Promise<CAPARecord> {
    const response = await apiClient.post('/quality/capas', data);
    return unwrapBody<CAPARecord>(response);
  }

  async updateCAPA(id: string, data: Partial<CAPARecord>): Promise<CAPARecord> {
    const response = await apiClient.put(`/quality/capas/${id}`, data);
    return unwrapBody<CAPARecord>(response);
  }

  async updateCAPAStatus(id: string, status: string): Promise<CAPARecord> {
    const response = await apiClient.post(`/quality/capas/${id}/update-status`, { status });
    return unwrapBody<CAPARecord>(response);
  }

  async submitCAPAForApproval(id: string): Promise<CAPARecord> {
    const response = await apiClient.post(`/quality/capas/${id}/submit-for-approval`);
    return unwrapBody<CAPARecord>(response);
  }

  async verifyCAPAEffectiveness(
    id: string,
    data: {
      effectiveness_verified: boolean;
      effectiveness_notes?: string;
      effectiveness_rating?: string;
      effectiveness_criteria?: string;
    }
  ): Promise<CAPARecord> {
    const response = await apiClient.post(`/quality/capas/${id}/verify-effectiveness`, data);
    return unwrapBody<CAPARecord>(response);
  }

  async closeCAPA(id: string, notes?: string): Promise<CAPARecord> {
    const response = await apiClient.post(`/quality/capas/${id}/close`, { notes });
    return unwrapBody<CAPARecord>(response);
  }

  async deleteCAPA(id: string): Promise<void> {
    await apiClient.delete(`/quality/capas/${id}`);
  }

  // Audit schedules
  async getAuditSchedules(): Promise<AuditScheduleRecord[]> {
    const response = await apiClient.get('/quality/audit-schedules');
    return unwrapList<AuditScheduleRecord>(response);
  }

  async createAuditSchedule(data: Partial<AuditScheduleRecord>): Promise<AuditScheduleRecord> {
    const response = await apiClient.post('/quality/audit-schedules', data);
    return unwrapBody<AuditScheduleRecord>(response);
  }

  // =====================================================
  // DEPARTMENT KPI METHODS
  // =====================================================
  async getDepartmentKPIs(filters?: { department_id?: string; is_active?: boolean }): Promise<any[]> {
    const response = await apiClient.get('/quality/kpis/department', { params: filters });
    return unwrapList(response);
  }

  async getDepartmentKPIById(id: string): Promise<any> {
    const response = await apiClient.get(`/quality/kpis/department/${id}`);
    return unwrapBody(response);
  }

  async createDepartmentKPI(data: any): Promise<any> {
    const response = await apiClient.post('/quality/kpis/department', data);
    return unwrapBody(response);
  }

  async updateDepartmentKPI(id: string, data: any): Promise<any> {
    const response = await apiClient.put(`/quality/kpis/department/${id}`, data);
    return unwrapBody(response);
  }

  async deleteDepartmentKPI(id: string): Promise<void> {
    await apiClient.delete(`/quality/kpis/department/${id}`);
  }

  // =====================================================
  // QUALITY PLAN KPI METHODS
  // =====================================================
  async getQualityPlanKPIs(qualityPlanId: string): Promise<any[]> {
    const response = await apiClient.get(`/quality/kpis/plan/${qualityPlanId}`);
    return unwrapList(response);
  }

  async assignKPIToQualityPlan(data: {
    quality_plan_id: string;
    department_kpi_id: string;
    target_value: number;
    tolerance?: number;
    measurement_frequency: string;
    is_mandatory?: boolean;
  }): Promise<any> {
    const response = await apiClient.post('/quality/kpis/plan', data);
    return unwrapBody(response);
  }

  async updateQualityPlanKPI(id: string, data: any): Promise<any> {
    const response = await apiClient.put(`/quality/kpis/plan/${id}`, data);
    return unwrapBody(response);
  }

  async removeKPIFromQualityPlan(id: string): Promise<void> {
    await apiClient.delete(`/quality/kpis/plan/${id}`);
  }

  // =====================================================
  // KPI MEASUREMENT METHODS
  // =====================================================
  async getKPIMeasurements(qualityPlanKPIId: string, filters?: { from_date?: string; to_date?: string }): Promise<any[]> {
    const response = await apiClient.get(`/quality/kpis/measurements/${qualityPlanKPIId}`, { params: filters });
    return unwrapList(response);
  }

  async recordKPIMeasurement(data: {
    quality_plan_kpi_id: string;
    measurement_date: string;
    measurement_period?: string;
    actual_value: number;
    target_value: number;
    notes?: string;
    corrective_action?: string;
    root_cause?: string;
  }): Promise<any> {
    const response = await apiClient.post('/quality/kpis/measurements', data);
    return unwrapBody(response);
  }

  async updateKPIMeasurement(id: string, data: any): Promise<any> {
    const response = await apiClient.put(`/quality/kpis/measurements/${id}`, data);
    return unwrapBody(response);
  }

  async verifyKPIMeasurement(id: string, verifiedBy: string, notes?: string): Promise<any> {
    const response = await apiClient.post(`/quality/kpis/measurements/${id}/verify`, {
      verified_by: verifiedBy,
      verification_notes: notes,
    });
    return unwrapBody(response);
  }

  // =====================================================
  // KPI ANALYTICS & REPORTING
  // =====================================================
  async getKPISummaryByDepartment(departmentId: string, period?: string): Promise<any> {
    const response = await apiClient.get('/quality/kpis/summary/department', {
      params: { department_id: departmentId, period }
    });
    return unwrapBody(response);
  }

  async getKPITrend(qualityPlanKPIId: string, fromDate?: string, toDate?: string): Promise<any> {
    const response = await apiClient.get(`/quality/kpis/trend/${qualityPlanKPIId}`, {
      params: { from_date: fromDate, to_date: toDate }
    });
    return unwrapBody(response);
  }

  async getOverallKPIPerformance(filters?: { company_id?: string; period?: string }): Promise<any> {
    const response = await apiClient.get('/quality/kpis/performance/overall', { params: filters });
    return unwrapBody(response);
  }
}

export const qualityService = new QualityService();

